import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import PageShell from "../components/PageShell";
import { CASES, CASE_LIST } from "../data/cases";

export default function CaseStudy() {
  const { slug } = useParams();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);
  const c = CASES[slug];
  if (!c) return <Navigate to="/" replace />;

  const idx = CASE_LIST.findIndex((x) => x.slug === slug);
  const next = CASE_LIST[(idx + 1) % CASE_LIST.length];

  return (
    <PageShell className="grain">
      <div className="pt-28 md:pt-36">
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Link
            to="/work"
            data-testid="case-back"
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-muted-x hover:text-primary-x transition-colors font-mono-x"
          >
            ← Back to work
          </Link>
        </div>

        {/* Hero block */}
        <section className="max-w-7xl mx-auto px-6 md:px-10 pt-10 md:pt-16 pb-16 md:pb-24">
          <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-end">
            <div className="md:col-span-8">
              <div className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x mb-5">
                {c.client} · {c.year}
              </div>
              <motion.h1
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                data-testid="case-title"
                className="font-display text-5xl md:text-7xl lg:text-8xl font-bold tracking-[-0.04em] leading-[0.92] text-balance"
              >
                {c.title}
              </motion.h1>
              <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.15, duration: 0.7 }}
                className="mt-8 text-lg md:text-xl text-secondary-x max-w-2xl leading-relaxed"
              >
                {c.summary}
              </motion.p>
            </div>
            <div className="md:col-span-4 grid grid-cols-2 gap-6">
              <Meta label="Tag" value={c.tag} />
              <Meta label="Duration" value={c.duration} />
              <Meta label="Role" value={c.role} className="col-span-2" />
            </div>
          </div>
        </section>

        {/* Hero image */}
        <section className="max-w-7xl mx-auto px-6 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative aspect-[16/9] overflow-hidden rounded-2xl border border-hairline bg-surface"
          >
            <img
              src={c.image}
              alt={c.title}
              className="absolute inset-0 w-full h-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent dark:from-black/40" />
          </motion.div>
        </section>

        {/* Outcome metrics */}
        <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {c.outcome.map((o, i) => (
              <motion.div
                key={o.label}
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.7 }}
                className="rounded-2xl border border-hairline bg-glass p-8 md:p-10"
              >
                <div className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x mb-4">
                  {o.label}
                </div>
                <div className="font-display text-5xl md:text-6xl font-medium tracking-tight">
                  {o.value}
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Problem / Approach */}
        <section className="max-w-7xl mx-auto px-6 md:px-10 pb-20 md:pb-28">
          <div className="grid md:grid-cols-12 gap-10 md:gap-16">
            <div className="md:col-span-4">
              <span className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x">
                ◇ The problem
              </span>
              <h2 className="mt-4 font-display text-3xl md:text-4xl font-medium tracking-tight leading-tight">
                Where they were stuck.
              </h2>
            </div>
            <p className="md:col-span-8 text-lg md:text-xl text-secondary-x leading-relaxed max-w-3xl">
              {c.problem}
            </p>
          </div>

          <div className="mt-20 md:mt-28 grid md:grid-cols-12 gap-10 md:gap-16">
            <div className="md:col-span-4">
              <span className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x">
                ◇ Our approach
              </span>
              <h2 className="mt-4 font-display text-3xl md:text-4xl font-medium tracking-tight leading-tight">
                How we shipped it.
              </h2>
            </div>
            <ol className="md:col-span-8 space-y-5 md:space-y-6 max-w-3xl">
              {c.approach.map((step, i) => (
                <motion.li
                  key={i}
                  initial={{ y: 20, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06, duration: 0.6 }}
                  className="flex gap-5 items-start"
                >
                  <span className="shrink-0 mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-glass border border-hairline text-[11px] font-mono-x text-secondary-x">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="text-base md:text-lg text-primary-x leading-relaxed">
                    {step}
                  </p>
                </motion.li>
              ))}
            </ol>
          </div>

          <div className="mt-20 md:mt-28 grid md:grid-cols-12 gap-10 md:gap-16">
            <div className="md:col-span-4">
              <span className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x">
                ◇ Stack
              </span>
              <h2 className="mt-4 font-display text-3xl md:text-4xl font-medium tracking-tight leading-tight">
                What it&apos;s made of.
              </h2>
            </div>
            <div className="md:col-span-8 flex flex-wrap gap-2">
              {c.stack.map((t) => (
                <span
                  key={t}
                  className="text-sm px-4 py-2 rounded-full border border-hairline text-primary-x bg-glass"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Next case study */}
        <section className="border-t border-hairline bg-surface">
          <Link
            to={`/work/${next.slug}`}
            data-testid="case-next"
            className="group block max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28"
          >
            <div className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x mb-4">
              Next case study
            </div>
            <div className="flex items-end justify-between gap-6 flex-wrap">
              <h3 className="font-display text-5xl md:text-7xl font-bold tracking-[-0.03em] leading-none">
                {next.title}
              </h3>
              <span className="inline-flex items-center gap-3 text-primary-x text-sm">
                {next.client}
                <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
              </span>
            </div>
          </Link>
        </section>

        {/* CTA */}
        <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32 text-center">
          <h2 className="font-display text-5xl md:text-7xl font-bold tracking-[-0.04em] leading-[0.95]">
            Have a problem this <br className="hidden md:block" />
            <em className="not-italic bg-gradient-to-br from-primary-x via-secondary-x to-muted-x bg-clip-text text-transparent">
              hard?
            </em>
          </h2>
          <Link
            to="/contact"
            data-testid="case-cta"
            className="mt-10 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium hover:opacity-90 transition-opacity"
            style={{ background: "var(--text-primary)", color: "var(--canvas)" }}
          >
            Start a project →
          </Link>
        </section>
      </div>
    </PageShell>
  );
}

function Meta({ label, value, className = "" }) {
  return (
    <div className={className}>
      <div className="text-[11px] tracking-[0.22em] uppercase text-muted-x font-mono-x mb-2">
        {label}
      </div>
      <div className="text-primary-x">{value}</div>
    </div>
  );
}
